import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCheck } from "@fortawesome/pro-solid-svg-icons";
import { faInstagram, faLinkedinIn } from "@fortawesome/free-brands-svg-icons";
import { toast } from "sonner";
import { z } from "zod";
import { track } from "@/lib/analytics/tracker";
import { EVENTS } from "@/lib/analytics/events";
import { getFirstTouch } from "@/lib/analytics/attribution";

// Lazy-loaded so the multi-step creator wizard — and the Supabase client +
// extra deps it pulls in — stay out of the eager landing bundle. The homepage
// only ever renders the member form, which never mounts these.
const CreatorRegistrationFlow = lazy(() => import("@/components/creator-registration/CreatorRegistrationFlow"));
const AbandonWarningDialog = lazy(() => import("@/components/creator-registration/AbandonWarningDialog"));

const MEMBER_SPORTS = ["Baseball", "Basketball", "Lacrosse", "Soccer", "Volleyball"];

// Staggered entrance for the post-submit confirmation: the cyan "stamp" springs
// in, then the headline and copy rise up just behind it. Reduced-motion users
// get the final state immediately (see the motion-reduce: classes below).
const CONFIRM_CSS = `
@keyframes confirmPop { 0% { opacity: 0; transform: scale(0.5); } 70% { opacity: 1; transform: scale(1.06); } 100% { opacity: 1; transform: scale(1); } }
@keyframes confirmRise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
`;

const phoneRegex = /^\d+$/;

const formatPhoneForDisplay = (digits: string): string => {
  const cleaned = digits.replace(/\D/g, "").slice(0, 11);
  if (cleaned.length === 0) return "";
  if (cleaned.length <= 3) return `(${cleaned}`;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  if (cleaned.length <= 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  return `+${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
};

const baseSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(50),
  lastName: z.string().trim().min(1, "Last name is required").max(50),
  email: z.string().trim().min(1, "Email is required").email("Please enter a valid email address").max(255),
  phone: z.string().trim().min(1, "Phone is required").min(10, "Phone number must be at least 10 digits").max(11, "Phone number cannot exceed 11 digits").regex(phoneRegex, "Please enter only numbers"),
});

const memberSchema = baseSchema.extend({
  // The page is organization-only now, so the org name is always required and
  // is what signals the form's audience (the old "who is this for?" radio is
  // gone).
  organizationName: z.string().trim().min(1, "Organization name is required").max(200),
  memberSports: z.array(z.string()).min(1, "Please select at least one sport"),
  otherSport: z.string().optional(),
}).refine((data) => {
  if (data.memberSports.includes("Other")) {
    return data.otherSport && data.otherSport.trim().length > 0;
  }
  return true;
}, {
  message: "Please specify the sport",
  path: ["otherSport"],
});

interface LeadGenerationFormProps {
  activeTab?: "member" | "creator";
  onTabChange?: (tab: "member" | "creator") => void;
  // Retained for API compatibility but always treated as hidden for the member
  // path. The creator path is reachable only from /become-a-creator, so the
  // tab switcher no longer renders here.
  isTabSwitcherHidden?: boolean;
  // Pre-fills the creator flow's email field (e.g. the marketing hero's email
  // pill carried down into the join-network form).
  initialEmail?: string;
}

const LeadGenerationForm = ({
  activeTab = "member",
  onTabChange,
  initialEmail,
}: LeadGenerationFormProps) => {
  const [tab, setTab] = useState<"member" | "creator">(activeTab);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDuplicate, setIsDuplicate] = useState(false);

  // Tab-switch guard wiring for the creator flow (kept intact so BecomeACreator
  // continues to behave as it did pre-redesign).
  const creatorShouldGuardRef = useRef(false);
  const startedRef = useRef(false);
  const [pendingTab, setPendingTab] = useState<"member" | "creator" | null>(null);

  useEffect(() => {
    setTab(activeTab);
  }, [activeTab]);

  // Member-only state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [memberSports, setMemberSports] = useState<string[]>([]);
  const [otherSport, setOtherSport] = useState("");

  const performTabChange = (newTab: "member" | "creator") => {
    setTab(newTab);
    setErrors({});
    onTabChange?.(newTab);
  };

  const toggleMemberSport = (sport: string) => {
    setMemberSports((prev) => {
      const newSports = prev.includes(sport) ? prev.filter((s) => s !== sport) : [...prev, sport];
      if (sport === "Other" && prev.includes("Other")) {
        setOtherSport("");
      }
      return newSports;
    });
  };

  const validateField = (fieldName: string, value: string) => {
    try {
      if (fieldName === "firstName") z.string().trim().min(1, "First name is required").max(50).parse(value);
      else if (fieldName === "lastName") z.string().trim().min(1, "Last name is required").max(50).parse(value);
      else if (fieldName === "email") z.string().trim().min(1, "Email is required").email("Please enter a valid email address").max(255).parse(value);
      else if (fieldName === "phone") z.string().trim().min(1, "Phone is required").min(10, "Phone number must be at least 10 digits").max(11, "Phone number cannot exceed 11 digits").regex(phoneRegex, "Please enter only numbers").parse(value);
      setErrors((prev) => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    } catch (err) {
      if (err instanceof z.ZodError) {
        setErrors((prev) => ({ ...prev, [fieldName]: err.errors[0]?.message || "Invalid input" }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (website) {
      track(EVENTS.BOT_SUSPECTED, { form_type: "member" });
      setIsSubmitted(true);
      return;
    }

    setErrors({});

    try {
      memberSchema.parse({
        firstName,
        lastName,
        email,
        phone,
        organizationName,
        memberSports,
        otherSport: memberSports.includes("Other") ? otherSport : undefined,
      });
    } catch (err) {
      if (err instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        err.errors.forEach((error) => {
          if (error.path[0]) newErrors[error.path[0] as string] = error.message;
        });
        track(EVENTS.LEAD_SUBMIT, {
          form_type: "member",
          status: "error",
          error_reason: "validation",
          invalid_fields: Object.keys(newErrors),
        });
        setErrors(newErrors);
        return;
      }
    }

    setIsLoading(true);

    try {
      const ft = getFirstTouch();
      const memberSportsForDb = memberSports
        .map((s) => (s === "Other" && otherSport.trim() ? otherSport.trim() : s))
        .filter((s) => s !== "Other" || otherSport.trim());

      const fullName = `${firstName.trim()} ${lastName.trim()}`;

      // Supabase client loaded on demand (first submit) so its ~30KB+ chunk
      // isn't part of the eager landing bundle — the form only needs it here.
      const { supabase } = await import("@/integrations/supabase/client");

      const { data: _data, error } = await supabase.functions.invoke("submit-lead", {
        body: {
          userType: "Member",
          fullName,
          email: email.trim(),
          phone: phone.trim(),
          // The member page is organization-only; the "who is this for?" UI was
          // removed but the value is preserved downstream (leads.role + the
          // Slack "Coverage" line) by sending a fixed "organization".
          role: "organization",
          sport: null,
          shootType: null,
          sportsCovered: memberSportsForDb,
          coverageType: "organization",
          // No scheduling step in the form anymore; always null.
          preferredMeetingTime: null,
          organizationName: organizationName.trim() || null,
          city: null,
          state: null,
          zipCode: null,
          travelRadius: null,
          utmSource: ft?.utm_source ?? null,
          utmMedium: ft?.utm_medium ?? null,
          utmCampaign: ft?.utm_campaign ?? null,
        },
      });

      if (error) {
        const fnError = error as { context?: Response };
        const status = fnError.context?.status;

        if (status === 409) {
          track(EVENTS.LEAD_SUBMIT, { form_type: "member", status: "duplicate" });
          setIsDuplicate(true);
          return;
        }
        if (status === 429) {
          track(EVENTS.LEAD_SUBMIT, { form_type: "member", status: "error", error_reason: "rate_limited" });
          toast.error("Too many submissions. Please try again in an hour.");
          return;
        }

        let errorMessage = "Something went wrong. Please try again.";
        try {
          if (fnError.context) {
            const errorBody = await fnError.context.json();
            if (errorBody?.error) errorMessage = errorBody.error;
          }
        } catch {
          // body not parseable
        }
        console.error("Unhandled edge function error:", { status, error });
        track(EVENTS.LEAD_SUBMIT, { form_type: "member", status: "error", error_reason: "server" });
        toast.error(errorMessage);
        return;
      }

      track(EVENTS.LEAD_SUBMIT, { form_type: "member", status: "success" });
      setIsSubmitted(true);
    } catch (error: unknown) {
      console.error("Error submitting lead:", error);
      track(EVENTS.LEAD_SUBMIT, { form_type: "member", status: "error", error_reason: "network" });
      const message = error instanceof Error ? error.message : "Something went wrong. Please try again.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Confirmation view ───────────────────────────────────────────────
  // Sales isn't on Microsoft Bookings yet, so a successful (or duplicate)
  // submission lands on an intentional confirmation moment instead of a
  // scheduling iframe: a cyan "stamp", a brand-voiced headline, and a clear
  // next-step expectation — personalized with the first name when we have it
  // (a honeypot bot submit may not).
  if ((isSubmitted || isDuplicate) && tab === "member") {
    const name = firstName.trim() ? `, ${firstName.trim()}` : "";
    const headline = isDuplicate ? "You're already on our list." : "You're all set.";
    const body = isDuplicate
      ? `Good to see you again${name}. We've got your details — a Fivestar specialist will follow up shortly to pick up where we left off.`
      : `Thanks${name}. Someone from our team will reach out soon. Keep an eye on your inbox.`;

    // Outline pills matching the secondary CTA style used elsewhere on the page.
    const socialCta =
      "inline-flex items-center gap-2 rounded-full border border-zinc-300 px-5 py-2.5 text-sm font-semibold text-zinc-800 transition-colors hover:bg-zinc-100 hover:border-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2";

    return (
      <div className="py-10 md:py-14 text-center">
        <style>{CONFIRM_CSS}</style>

        {/* role="status" announces the success + next step to screen readers when
            the form is swapped out for this panel (WCAG 4.1.3 status messages).
            The social CTAs sit outside it so the announcement stays focused on
            the confirmation, with the links still reachable by normal nav. */}
        <div role="status" aria-live="polite">
          {/* Cyan "stamp": a soft halo behind a solid disc with a check + brand glow. */}
          <div className="relative mx-auto mb-7 inline-flex items-center justify-center opacity-0 animate-[confirmPop_520ms_ease-out_forwards] motion-reduce:animate-none motion-reduce:opacity-100">
            <span aria-hidden="true" className="absolute -inset-2 rounded-full bg-cyan/15" />
            <span className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-cyan shadow-[0_10px_30px_-6px_rgba(68,194,227,0.55)]">
              <FontAwesomeIcon icon={faCheck} className="h-7 w-7 text-black" />
            </span>
          </div>

          {/* h2 (not h3): keeps a valid outline both on the homepage (sibling to
              the LeadFormSection h2) and on the standalone /become-a-member page
              (whose page heading is an h1, so h1 -> h2 with no skipped level). */}
          <h2 className="text-3xl md:text-4xl font-black italic uppercase tracking-tight leading-[1.05] text-black opacity-0 translate-y-2 animate-[confirmRise_560ms_ease-out_120ms_forwards] motion-reduce:animate-none motion-reduce:opacity-100 motion-reduce:translate-y-0">
            {headline}
          </h2>

          <p className="mx-auto mt-4 max-w-md text-base md:text-lg leading-relaxed text-zinc-600 opacity-0 translate-y-2 animate-[confirmRise_560ms_ease-out_220ms_forwards] motion-reduce:animate-none motion-reduce:opacity-100 motion-reduce:translate-y-0">
            {body}
          </p>
        </div>

        {/* Something to explore while they wait to hear back. */}
        <div className="mt-8 opacity-0 translate-y-2 animate-[confirmRise_560ms_ease-out_320ms_forwards] motion-reduce:animate-none motion-reduce:opacity-100 motion-reduce:translate-y-0">
          <p className="text-sm text-zinc-500">While you wait, get to know us</p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://www.instagram.com/fivestarvids"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="See our work on Instagram (opens in a new tab)"
              className={socialCta}
            >
              <FontAwesomeIcon icon={faInstagram} className="h-4 w-4" />
              See our work
            </a>
            <a
              href="https://www.linkedin.com/company/fivestar-app/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Fivestar on LinkedIn (opens in a new tab)"
              className={socialCta}
            >
              <FontAwesomeIcon icon={faLinkedinIn} className="h-4 w-4" />
              Follow on LinkedIn
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Creator branch ──────────────────────────────────────────────────
  // Untouched visual chrome: keeps the dark-glass styling that BecomeACreator
  // relies on. The creator path is no longer reachable from the homepage.
  if (tab === "creator") {
    return (
      <div
        className="max-w-lg mx-auto border border-white/20 rounded-2xl p-6"
        style={{
          "--autofill-bg": "rgba(255, 255, 255, 0.05)",
          "--autofill-border": "rgba(255, 255, 255, 0.1)",
        } as React.CSSProperties}
      >
        <Suspense fallback={<div className="py-12 text-center text-white/60">Loading…</div>}>
          <CreatorRegistrationFlow shouldGuardRef={creatorShouldGuardRef} initialEmail={initialEmail} />
          <AbandonWarningDialog
            isOpen={pendingTab !== null}
            onConfirm={() => {
              const target = pendingTab;
              setPendingTab(null);
              if (target) performTabChange(target);
            }}
            onCancel={() => setPendingTab(null)}
          />
        </Suspense>
      </div>
    );
  }

  // ── Member form (light visual language) ─────────────────────────────
  // No outer card chrome: the parent (LeadFormSection / BecomeAMember) is
  // expected to wrap this in a white card. Inputs stay pill-shaped from the
  // shared Input component; the focus ring is cyan; CTA is lime-burst.
  return (
    <form
      onSubmit={handleSubmit}
      onFocus={() => {
        if (startedRef.current) return;
        startedRef.current = true;
        track(EVENTS.LEAD_FORM_START, { form_type: "member" });
      }}
      className="space-y-4"
      style={{
        "--autofill-bg": "#ffffff",
        "--autofill-border": "#e4e4e7",
        "--autofill-text": "#18181b",
        "--autofill-caret": "#18181b",
      } as React.CSSProperties}
    >
      {/* Honeypot */}
      <div className="absolute opacity-0 pointer-events-none" style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          name="website"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="firstName" isRequired className="text-zinc-700 text-sm font-medium">First Name</Label>
          <Input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={(e) => validateField("firstName", e.target.value)}
            placeholder="First name"
            className="bg-white border-zinc-200 text-black placeholder:text-zinc-400 mt-1.5 px-4"
          />
          {errors.firstName && <p className="text-red-600 text-xs mt-1.5">{errors.firstName}</p>}
        </div>
        <div>
          <Label htmlFor="lastName" isRequired className="text-zinc-700 text-sm font-medium">Last Name</Label>
          <Input
            id="lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onBlur={(e) => validateField("lastName", e.target.value)}
            placeholder="Last name"
            className="bg-white border-zinc-200 text-black placeholder:text-zinc-400 mt-1.5 px-4"
          />
          {errors.lastName && <p className="text-red-600 text-xs mt-1.5">{errors.lastName}</p>}
        </div>
      </div>

      <div>
        <Label htmlFor="email" isRequired className="text-zinc-700 text-sm font-medium">Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={(e) => validateField("email", e.target.value)}
          placeholder="you@yourorg.com"
          className="bg-white border-zinc-200 text-black placeholder:text-zinc-400 mt-1.5 px-4"
        />
        {errors.email && <p className="text-red-600 text-xs mt-1.5">{errors.email}</p>}
      </div>

      <div>
        <Label htmlFor="phone" isRequired className="text-zinc-700 text-sm font-medium">Phone</Label>
        <Input
          id="phone"
          type="tel"
          inputMode="numeric"
          value={formatPhoneForDisplay(phone)}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "").slice(0, 11);
            setPhone(value);
          }}
          onBlur={() => validateField("phone", phone)}
          placeholder="(555) 123-4567"
          className="bg-white border-zinc-200 text-black placeholder:text-zinc-400 mt-1.5 px-4"
        />
        {errors.phone && <p className="text-red-600 text-xs mt-1.5">{errors.phone}</p>}
      </div>

      <div>
        <Label htmlFor="organizationName" isRequired className="text-zinc-700 text-sm font-medium">Your Organization's Name</Label>
        <Input
          id="organizationName"
          type="text"
          value={organizationName}
          onChange={(e) => setOrganizationName(e.target.value)}
          placeholder="e.g., Northside Youth Soccer"
          className="bg-white border-zinc-200 text-black placeholder:text-zinc-400 mt-1.5 px-4"
        />
        {errors.organizationName && <p className="text-red-600 text-xs mt-1.5">{errors.organizationName}</p>}
      </div>

      <div>
        <Label isRequired className="text-zinc-700 text-sm font-medium mb-2 block">Sport(s) or Event Type</Label>
        <div className="grid grid-cols-2 gap-2">
          {MEMBER_SPORTS.map((sport) => {
            const checked = memberSports.includes(sport);
            return (
              <label
                key={sport}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${
                  checked
                    ? "bg-cyan/10 border-cyan ring-1 ring-cyan/40"
                    : "bg-white border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
                }`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggleMemberSport(sport)}
                  className="border-zinc-300 data-[state=checked]:bg-cyan data-[state=checked]:border-cyan data-[state=checked]:text-black"
                />
                <span className="text-sm text-zinc-800 leading-tight">{sport}</span>
              </label>
            );
          })}
          <label
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${
              memberSports.includes("Other")
                ? "bg-cyan/10 border-cyan ring-1 ring-cyan/40"
                : "bg-white border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
            }`}
          >
            <Checkbox
              checked={memberSports.includes("Other")}
              onCheckedChange={() => toggleMemberSport("Other")}
              className="border-zinc-300 data-[state=checked]:bg-cyan data-[state=checked]:border-cyan data-[state=checked]:text-black"
            />
            <span className="text-sm text-zinc-800 leading-tight">Other</span>
          </label>
          {memberSports.includes("Other") && (
            <div className="col-span-2 mt-1">
              <Input
                type="text"
                value={otherSport}
                onChange={(e) => setOtherSport(e.target.value)}
                placeholder="Please specify the sport or event type"
                className="bg-white border-zinc-200 text-black placeholder:text-zinc-400 px-4"
              />
            </div>
          )}
        </div>
        {errors.memberSports && <p className="text-red-600 text-xs mt-1.5">{errors.memberSports}</p>}
        {errors.otherSport && <p className="text-red-600 text-xs mt-1.5">{errors.otherSport}</p>}
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="group w-full inline-flex items-center justify-center gap-2 bg-cyan hover:bg-cyan/90 text-black font-black italic uppercase tracking-wide pl-8 pr-9 py-4 text-base shadow-[0_4px_14px_rgba(68,194,227,0.25)] hover:shadow-[0_6px_22px_rgba(68,194,227,0.32)] transition-all rounded-full mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          "Submitting..."
        ) : (
          <>
            <span>Request Info</span>
            <FontAwesomeIcon
              icon={faArrowRight}
              className="w-4 h-4 transition-transform group-hover:translate-x-0.5"
            />
          </>
        )}
      </button>
    </form>
  );
};

export default LeadGenerationForm;
